
import { Component, Vue } from 'vue-property-decorator'

import Pagination from '@/components/Pagination/index.vue'
import { Card, Gift, MultiChoiceCard } from '@/models'
import request from '@/utils/request'

@Component({
  name: 'GiftsList',
  components: {
    GiftsListFilters: () => import('./components/GiftsListFilters.vue'),
    ResendGiftEmailDialog: () => import('./ResendGiftEmailDialog.vue'),
    Pagination
  }
})
export default class extends Vue {
  gifts: Gift[] = []
  giftLoading = true
  totalGifts = 0
  headersByDate: any[] = []
  private giftDetailsById: Record<string, Gift> = {}
  private giftDetailsLoading: Record<string, boolean> = {}
  showResendEmailDialog = false
  resendEmailGift: Gift | null = null
  pdfLoadingGiftId: string | null = null

  filters: any = {
    email: null,
    firstName: null,
    lastName: null,
    dateRange: null,
    status: null
  }
  pagination = {
    page: 1,
    limit: 20
  }

  created() {
    this.getGifts()
  }

  public refresh(): void {
    this.pagination.page = 1
    this.getGifts()
  }

  private async getGifts() {
    this.giftLoading = true

    const createdAtGte = this.filters.dateRange ? this.$moment(String(this.filters.dateRange![0])).format('YYYY-MM-DD HH:mm:ss') : ''
    const createdAtLte = this.filters.dateRange ? this.$moment(String(this.filters.dateRange![1])).format('YYYY-MM-DD HH:mm:ss') : ''

    const { data, meta } = await Gift
        .includes([{ cards: ['giftCard'] }, 'recipient'])
        .where({
          hasCards: true,
          recipientEmail: { prefix: this.filters.email },
          recipientFirstname: { prefix: this.filters.firstName },
          recipientLastname: { prefix: this.filters.lastName },
          status: this.filters.status,
          createdAt: { gte: createdAtGte, lte: createdAtLte }
        })
        .order({ createdAt: 'desc' })
        .page(this.pagination.page)
        .per(this.pagination.limit)
        .stats({ total: 'count' })
        .all()

    this.gifts = data
    this.totalGifts = meta.stats.total.count
    this.giftDetailsById = {}
    this.giftDetailsLoading = {}
    this.giftPerDate()
    this.giftLoading = false
  }

  private giftPerDate() {
    this.headersByDate = []
    this.gifts.map((e, index) => {
      if (this.gifts[index - 1] && this.$moment(this.gifts[index - 1].createdAt).format('L') !== this.$moment(e.createdAt).format('L')) {
        this.headersByDate.push(index)
      }
    })
  }

  handleSearch(filters: any): void {
    this.filters = filters
    this.pagination.page = 1
    this.gifts = []
    this.giftDetailsById = {}
    this.giftDetailsLoading = {}
    this.getGifts()
  }

  private detailGift(gift: Gift): Gift {
    return this.giftDetailsById[gift.id] || gift
  }

  private isDetailLoading(gift: Gift): boolean {
    return !!this.giftDetailsLoading[gift.id]
  }

  private hasLoadedGiftCards(gift: Gift): boolean {
    return gift.cards?.length > 0 && gift.cards.every(card => !!card.giftCard?.name)
  }

  private async loadGiftDetail(gift: Gift): Promise<void> {
    if (this.giftDetailsById[gift.id] || this.giftDetailsLoading[gift.id]) return

    if (this.hasLoadedGiftCards(gift)) {
      this.$set(this.giftDetailsById, gift.id, gift)
      return
    }

    this.$set(this.giftDetailsLoading, gift.id, true)
    try {
      // Ne pas inclure multi_choice_cards sur Gift : le backend tente un filtre gift_id invalide.
      // On lit les IDs depuis relationships, puis charge chaque MCC via GET /multi_choice_cards/:id.
      const payload = await request.get(`/market/v1/gifts/${gift.id}`, {
        params: {
          include: 'recipient,cards,cards.gift_card'
        }
      })

      const giftDetail = new Gift()
      giftDetail.fromJsonapi(payload.data, payload)

      const multiChoiceCardIds = (payload.data.relationships?.multi_choice_cards?.data ?? [])
        .map((ref: { id: string }) => ref.id)

      if (multiChoiceCardIds.length > 0) {
        const multiChoiceCards = await Promise.all(
          multiChoiceCardIds.map(async (id: string) => {
            const { data } = await MultiChoiceCard
              .includes(['multiChoiceGiftCard', 'dotationOperation'])
              .find(id)
            return data
          })
        )
        giftDetail.multiChoiceCards = multiChoiceCards
      }

      this.$set(this.giftDetailsById, gift.id, giftDetail)
    } finally {
      this.$set(this.giftDetailsLoading, gift.id, false)
    }
  }

  private giftKind(gift: Gift): 'multi_choice' | 'gift_card' | 'unknown' {
    if (gift.multiChoiceCards?.length > 0) return 'multi_choice'
    if (gift.cards?.length > 0) return 'gift_card'
    return 'unknown'
  }

  private emailStatus(gift: Gift): string {
    if (gift.emailFailedAt) {
      return `Échec d'envoi`
    }
    if (gift.emailOpenedAt) {
      return `Ouvert le ${this.$moment(gift.emailOpenedAt).format('L [à] LT')}`
    }
    if (gift.emailDeliveredAt) {
      return `Reçu le ${this.$moment(gift.emailDeliveredAt).format('L [à] LT')}`
    }
    return 'Envoyé'
  }

  private resendLimitReached(gift: Gift): boolean {
    const count = gift.emailResendCount || 0
    return count >= Gift.MAX_EMAIL_RESENDS
  }

  private resendEmailTooltip(gift: Gift): string {
    if (this.resendLimitReached(gift)) {
      return String(this.$t('gifts.resendEmailDialog.limitReachedTooltip'))
    }
    return String(this.$t('gifts.resendEmailDialog.actionTooltip'))
  }

  private openResendEmailDialog(gift: Gift): void {
    this.resendEmailGift = gift
    this.showResendEmailDialog = true
  }

  private closeResendEmailDialog(): void {
    this.showResendEmailDialog = false
    this.resendEmailGift = null
  }

  private redemptionPdfLabel(gift: Gift): string {
    if (gift.redemptionCodeGeneratedAt) {
      return String(this.$t('gifts.redemptionPdf.downloadBtn'))
    }
    return String(this.$t('gifts.redemptionPdf.generateBtn'))
  }

  private redemptionPdfTooltip(gift: Gift): string {
    if (gift.redemptionCodeGeneratedAt) {
      return String(this.$t('gifts.redemptionPdf.downloadTooltip'))
    }
    return String(this.$t('gifts.redemptionPdf.generateTooltip'))
  }

  private async downloadRedemptionPdf(gift: Gift): Promise<void> {
    const alreadyGenerated = !!gift.redemptionCodeGeneratedAt

    this.pdfLoadingGiftId = gift.id
    try {
      await Gift.downloadRedemptionPdf(gift.id)
      if (!alreadyGenerated) {
        this.getGifts()
      }
    } catch (err) {
      this.$notify({
        title: String(this.$t('gifts.redemptionPdf.notification.error.title')),
        message: String(this.$t('gifts.redemptionPdf.notification.error.fallback')),
        type: 'error',
        duration: 5000
      })
    } finally {
      this.pdfLoadingGiftId = null
    }
  }

  private formatDate(date: string): string {
    const formatted = this.$moment(date).format('L')
    return formatted === 'Invalid date' ? '' : formatted
  }

  private cardInGift(cards: Card[]): any {
    const tempList: any = {}

    cards.forEach((card: Card) => {
      if (!card.giftCard?.id) return

      const cardCharacteristic = `${card.giftCard.id}_${card.amount}`
      if (!tempList[cardCharacteristic]) tempList[cardCharacteristic] = [card]
      else tempList[cardCharacteristic].push(card)
    })

    return tempList
  }

  private writeCardDetails(cards: Card[]): string {
    if (cards.length === 0) return 'N/A'
    const usage = cards[0].usageStatus ? ` (${cards[0].usageStatus})` : ''
    return `${cards.length} carte${cards.length > 1 ? 's' : ''} ${cards[0].giftCard.name} d'un montant égal à ${this.$n(cards[0].amount, 'currency')}${usage}`
  }
}
